import React, { useEffect, useState } from 'react';

/**
 * OrientationEnforcer
 *
 * Blocks the UI on handheld devices when the screen is in portrait mode and
 * prompts the user to rotate to landscape. Attempts to lock orientation where
 * supported (installed PWA / some Android browsers with user gesture).
 */
const OrientationEnforcer: React.FC = () => {
  const [blocked, setBlocked] = useState(false);
  const [inApp, setInApp] = useState(false);

  useEffect(() => {
    const isHandheld = () => {
      const uaMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      return uaMobile || coarse;
    };

    const isInAppBrowser = () => {
      // Common social in-app browser signatures (iOS/Android)
      // Facebook/Instagram/Twitter/X/LinkedIn/Reddit etc. expand as needed
      const ua = navigator.userAgent || '';
      return /FBAN|FBAV|Instagram|Line\/|WhatsApp|Twitter|Snapchat|LinkedInApp|TikTok|Pinterest|Reddit/i.test(ua);
    };

    const update = () => {
      const isPortrait = window.matchMedia('(orientation: portrait)').matches;
      const handheld = isHandheld();
      const iab = handheld && isInAppBrowser();
      setInApp(iab);
      // Do not block inside social in-app browsers; they often cannot rotate
      setBlocked(handheld && isPortrait && !iab);
    };

    // Initial check
    update();

    // Listen for orientation and size changes
    const mql = window.matchMedia('(orientation: portrait)');
    const onChange = () => update();
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if ((mql as any).addListener) (mql as any).addListener(onChange);

    window.addEventListener('resize', update);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if ((mql as any).removeListener) (mql as any).removeListener(onChange);
      window.removeEventListener('resize', update);
    };
  }, []);

  const tryLockLandscape = async () => {
    try {
      // Fullscreen first increases chances orientation lock is allowed on some browsers
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      }
      const scr: any = screen as any;
      if (scr?.orientation?.lock) {
        await scr.orientation.lock('landscape');
      }
    } catch (err) {
      // Some browsers (notably iOS Safari) do not allow programmatic orientation lock
      console.warn('Orientation lock not permitted:', err);
    }
  };

  const openInBrowser = () => {
    // Open a new tab which on many platforms prompts to choose an external browser
    // Some in-app browsers still intercept _blank; still useful for many Android cases
    try {
      window.open(window.location.href, '_blank', 'noopener');
    } catch {}
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied. Please open it in your device\'s browser (Safari/Chrome).');
    } catch {
      alert('Copy failed. Long-press the address bar to copy the URL.');
    }
  };

  if (!blocked) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 text-theme-primary p-6">
      <div className="max-w-md text-center space-y-4">
        <div className="text-3xl font-semibold">Rotate your device</div>
        {inApp ? (
          <>
            <p className="text-base text-theme-primary/90">
              You are viewing this inside a social app browser that prevents landscape. Please open in your
              device's browser to continue.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={openInBrowser}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Open in browser
              </button>
              <button
                type="button"
                onClick={copyLink}
                className="px-4 py-2 rounded bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                Copy link
              </button>
            </div>
            <p className="text-xs text-theme-primary/60">
              Tip: Use the ••• menu and choose “Open in Safari/Chrome” if available.
            </p>
          </>
        ) : (
          <>
            <p className="text-base text-theme-primary/90">
              This experience is optimized for landscape. Please rotate your phone or tablet to continue.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={tryLockLandscape}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Try rotate automatically
              </button>
            </div>
            <p className="text-xs text-theme-primary/60">
              If the button does nothing, rotate your device manually. Some mobile browsers do not allow websites to change
              orientation automatically unless installed as an app.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OrientationEnforcer;
